import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { ApiError, errorMessage } from '@/api/problem';

/**
 * Converts an RFC 6901 JSON pointer from a problem+json `errors[].pointer` into a react-hook-form field path.
 * `/channels/whatsapp/wabaId` → `channels.whatsapp.wabaId`; `~1` and `~0` are unescaped. Empty pointer → null.
 */
export function pointerToPath(pointer: string | null | undefined): string | null {
  if (!pointer || pointer === '/' || pointer === '#') return null;
  const p = pointer.startsWith('#') ? pointer.slice(1) : pointer;
  const parts = p
    .split('/')
    .filter((s, i) => !(i === 0 && s === ''))
    .map((s) => s.replace(/~1/g, '/').replace(/~0/g, '~'));
  if (parts.length === 0) return null;
  return parts.join('.');
}

export interface ProblemMappingOptions {
  /** Field names the form knows; pointers resolving elsewhere become root errors. */
  fields: readonly string[];
  /** Optional renames from API path (dot form, case-insensitive) to form field name. */
  aliases?: Readonly<Record<string, string>>;
}

export interface MappedProblem {
  fieldErrors: Record<string, string>;
  rootMessage: string | null;
}

/** Splits an API error into per-field messages (by pointer) and one root message for everything else. */
export function mapProblem(err: unknown, opts: ProblemMappingOptions): MappedProblem {
  const fieldErrors: Record<string, string> = {};
  const unmatched: string[] = [];
  const known = new Map(opts.fields.map((f) => [f.toLowerCase(), f]));
  const aliases = new Map(Object.entries(opts.aliases ?? {}).map(([k, v]) => [k.toLowerCase(), v]));

  if (err instanceof ApiError) {
    for (const e of err.problem.errors ?? []) {
      const path = pointerToPath(e.pointer);
      const key = path?.toLowerCase();
      // Accept the exact field, an alias, or the closest known ancestor (e.g. /locales/2 → locales).
      let target: string | undefined;
      if (key) {
        target = aliases.get(key) ?? known.get(key);
        if (!target) {
          const segs = key.split('.');
          for (let i = segs.length - 1; i > 0 && !target; i--) {
            const prefix = segs.slice(0, i).join('.');
            target = aliases.get(prefix) ?? known.get(prefix);
          }
        }
      }
      if (target && !fieldErrors[target]) fieldErrors[target] = e.message;
      else if (!target) unmatched.push(e.message);
    }
  }

  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  let rootMessage: string | null = unmatched.length > 0 ? unmatched.join(' · ') : null;
  if (!rootMessage && !hasFieldErrors) rootMessage = errorMessage(err);
  return { fieldErrors, rootMessage };
}

/** Applies a problem+json error to a react-hook-form instance: field errors by pointer, the rest as `root`. */
export function applyProblemToForm<T extends FieldValues>(
  form: Pick<UseFormReturn<T>, 'setError'>,
  err: unknown,
  opts: ProblemMappingOptions,
): void {
  const { fieldErrors, rootMessage } = mapProblem(err, opts);
  for (const [field, message] of Object.entries(fieldErrors)) form.setError(field as Path<T>, { type: 'server', message });
  if (rootMessage) form.setError('root', { type: 'server', message: rootMessage });
}
